import { OperatorGamePopulated } from "../shared/interfaces";
import { getEffectiveGameStartMs, isOperatorDashboardLivePhase } from "./gameEffectiveStart";
import { normalizeOperatorEmail } from "./operatorEmail";
import { isParkingSubmissionOpen } from "./parkingSubmissionWindow";

type GameManagerFields = Pick<OperatorGamePopulated, "gameManagers" | "gameManager">;

/** Super-admin game manager emails: uses gameManagers when present, else legacy gameManager. */
export function effectiveGameManagerEmails(game: GameManagerFields): string[] {
  const fromArr = (game.gameManagers || [])
    .map((e) => normalizeOperatorEmail(String(e)))
    .filter((e): e is string => Boolean(e));
  if (fromArr.length > 0) {
    return Array.from(new Set(fromArr));
  }
  const legacy = normalizeOperatorEmail(String(game.gameManager || ""));
  return legacy ? [legacy] : [];
}

export function isViewerGameManagerForGame(
  game: GameManagerFields,
  email: string | null | undefined,
): boolean {
  const e = normalizeOperatorEmail(email || "");
  if (!e) return false;
  return effectiveGameManagerEmails(game).includes(e);
}

export function isOperatorAssignedToGame(
  game: Pick<OperatorGamePopulated, "assignedOperators"> | null,
  email: string | null | undefined,
): boolean {
  if (!game || !email) return false;
  const e = normalizeOperatorEmail(email);
  if (!e) return false;
  return (game.assignedOperators || []).some(
    (a) => normalizeOperatorEmail(String(a)) === e,
  );
}

/** Assigned operator or super-admin game manager may finish a live game via checklist. */
export function canOperatorCompleteGameChecklist(
  game: Pick<
    OperatorGamePopulated,
    | "assignedOperators"
    | "gameManagers"
    | "gameManager"
    | "finishDate"
    | "archived"
    | "startDate"
    | "startTime"
  > | null,
  viewerEmail: string | null | undefined,
  options?: { isSuperAdmin?: boolean },
): boolean {
  if (!game || !viewerEmail) return false;
  if (game.archived || game.finishDate) return false;
  const kickoff = getEffectiveGameStartMs({
    startDate: game.startDate,
    startTime: game.startTime,
  });
  if (Date.now() < kickoff) return false;

  if (isOperatorAssignedToGame(game, viewerEmail)) return true;
  if (options?.isSuperAdmin && isViewerGameManagerForGame(game, viewerEmail)) {
    return true;
  }
  return false;
}

/**
 * Assigned operator, SnapSquad, or booth staff may submit parking for 48 hours
 * after the game's finish time.
 */
export function canSubmitGameParking(
  game: Pick<OperatorGamePopulated, "assignedOperators" | "finishDate"> | null,
  viewerEmail: string | null | undefined,
  now = Date.now(),
): boolean {
  if (!game || !isOperatorAssignedToGame(game, viewerEmail)) return false;
  const finishDate = Number(game.finishDate);
  if (!finishDate) return false;
  return isParkingSubmissionOpen(finishDate, now);
}

export function canViewerFinishGame(
  game: Pick<
    OperatorGamePopulated,
    "assignedOperators" | "gameManagers" | "gameManager"
  > | null,
  viewerEmail: string | null | undefined,
  isSuperAdmin = false,
): boolean {
  if (!game || !viewerEmail) return false;
  if (isOperatorAssignedToGame(game, viewerEmail)) return true;
  return isSuperAdmin && isViewerGameManagerForGame(game, viewerEmail);
}

type OperatorMomentsGateGame = {
  finishDate?: number | null;
  archived?: boolean;
  status?: string;
  startDate?: number;
  startTime?: string | null;
  operatorMarkedLiveAt?: number | null;
};

/**
 * Games-list rows carry a computed `status`. A game loaded by id does not, so derive the
 * same upcoming / live / finished value from archive, finish, and the dashboard live window.
 */
function operatorMomentsGateStatus(game: OperatorMomentsGateGame): string | undefined {
  if (game.status) return game.status;
  if (game.startDate == null) return undefined;
  if (game.archived) return "archived";
  if (game.finishDate) return "finished";
  const live = isOperatorDashboardLivePhase({
    startDate: game.startDate,
    startTime: game.startTime,
    operatorMarkedLiveAt: game.operatorMarkedLiveAt,
  });
  return live ? "ongoing" : "upcoming";
}

/** After Finish game (or archive), operators cannot open Moments / camera trigger. */
export function isGameClosedForOperatorMoments(
  game: OperatorMomentsGateGame | null | undefined,
): boolean {
  if (!game) return false;
  if (game.archived) return true;
  if (game.finishDate) return true;
  return operatorMomentsGateStatus(game) === "finished";
}

/** Upcoming games stay closed to operators and assigned staff until the game is live. */
export function isUpcomingGameBlockedForOperator(
  game: OperatorMomentsGateGame | null | undefined,
): boolean {
  if (!game || isGameClosedForOperatorMoments(game)) return false;
  return operatorMomentsGateStatus(game) === "upcoming";
}
